import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { C, field, btnPrimary, softShadow } from "../lib/theme";
import { toast } from "sonner";
import { Save, Clock, Plus, X, CreditCard, CheckCircle2, AlertCircle, ExternalLink, Link2, Copy, Download, Code2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const ACCENT_PRESETS = ["#FF5A36", "#12715A", "#F4B740", "#2563EB", "#7C3AED", "#DB2777", "#0D9488", "#141210"];

export default function OwnerSettings() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripeKey, setStripeKey] = useState("");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get("/owner/restaurant").then((res) => setR(res.data)).catch(() => toast.error("Errore caricamento"));
  }, []);

  const upd = (k, v) => setR((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/owner/restaurant", {
        name: r.name, description: r.description, address: r.address, phone: r.phone,
        whatsapp: r.whatsapp, logo_image: r.logo_image, cover_image: r.cover_image,
        accent_color: r.accent_color,
        deposit_per_person: Number(r.deposit_per_person),
        max_covers_per_slot: Number(r.max_covers_per_slot),
        min_party_size: Number(r.min_party_size), max_party_size: Number(r.max_party_size),
        opening_ranges: r.opening_ranges || [],
      });
      setR(data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const connectStripe = async () => {
    if (!stripeKey.trim().startsWith("sk_")) { toast.error("La chiave deve iniziare con 'sk_'"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post("/owner/stripe/connect", { secret_key: stripeKey.trim() });
      setR(data);
      setStripeKey("");
      toast.success("Pagamenti collegati con successo");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  const disconnectStripe = async () => {
    try {
      const { data } = await api.post("/owner/stripe/disconnect");
      setR(data);
      toast.success("Pagamenti scollegati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!r) return <div className="min-h-[50vh] flex items-center justify-center font-semibold" style={{ color: C.CORAL }}>Caricamento…</div>;

  const bookingLink = `${window.location.origin}/prenota/${r.id}`;
  const copyLink = () => { navigator.clipboard.writeText(bookingLink); toast.success("Link copiato negli appunti"); };
  const downloadQR = () => {
    const canvas = document.getElementById("booking-qr-canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${(r.name || "ristorante").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
    toast.success("QR scaricato");
  };
  const embedCode = `<a href="${bookingLink}" target="_blank" rel="noopener" style="background:${r.accent_color || "#FF5A36"};color:#fff;padding:14px 28px;border-radius:999px;font-weight:700;text-decoration:none;font-family:sans-serif">Prenota un tavolo</a>`;
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); toast.success("Codice per il sito copiato"); };
  const genSlots = (ranges) => {
    const out = new Set();
    (ranges || []).forEach((rg) => {
      if (!rg.start || !rg.end) return;
      const [sh, sm] = rg.start.split(":").map(Number);
      const [eh, em] = rg.end.split(":").map(Number);
      let cur = sh * 60 + sm; const end = eh * 60 + em;
      while (cur <= end) { out.add(`${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`); cur += 30; }
    });
    return [...out].sort();
  };
  const updateRange = (i, k, v) => { const rr = [...(r.opening_ranges || [])]; rr[i] = { ...rr[i], [k]: v }; upd("opening_ranges", rr); };
  const removeRange = (i) => upd("opening_ranges", (r.opening_ranges || []).filter((_, idx) => idx !== i));
  const addRange = () => upd("opening_ranges", [...(r.opening_ranges || []), { start: "19:00", end: "22:30" }]);
  const generatedSlots = genSlots(r.opening_ranges);

  const secondaryBtn = "px-4 py-2.5 rounded-full bg-white border font-semibold flex items-center gap-1.5 text-sm transition-colors hover:border-[#FF5A36]";

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 space-y-8">
      <div>
        <h1 className="font-display text-4xl font-extrabold mb-1" style={{ color: C.INK }}>Impostazioni ristorante</h1>
        <p style={{ color: C.MUTED }}>Gestisci i dettagli, la caparra, le fasce orarie e i pagamenti.</p>
      </div>

      {/* Booking link */}
      <div data-testid="booking-link-card" className="bg-white rounded-3xl p-6 sm:p-8" style={softShadow}>
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-5 h-5" style={{ color: C.CORAL }} />
          <h2 className="font-display text-2xl font-extrabold" style={{ color: C.INK }}>Il tuo link di prenotazione</h2>
        </div>
        <p className="text-sm mb-5" style={{ color: C.MUTED }}>Condividi questo link privato con i tuoi clienti (sito, social, WhatsApp). Prenoteranno solo dal tuo ristorante.</p>
        <div className="flex gap-2">
          <input data-testid="booking-link" readOnly value={bookingLink} className={`${field} font-mono text-sm`} />
          <button data-testid="copy-link" onClick={copyLink} className={`${secondaryBtn} shrink-0`} style={{ borderColor: C.BORDER, color: C.CORAL }}>
            <Copy className="w-4 h-4" /> Copia
          </button>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-6 items-start">
          <div className="bg-white p-3 rounded-2xl border shrink-0" style={{ borderColor: C.BORDER }} data-testid="booking-qr">
            <QRCodeCanvas id="booking-qr-canvas" value={bookingLink} size={132} level="M" />
          </div>
          <div className="flex-1 space-y-3">
            <p className="text-sm" style={{ color: C.MUTED }}>Scarica il QR code per menù, vetrina o volantini, oppure copia il codice da incollare nel tuo sito.</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="download-qr" onClick={downloadQR} className={secondaryBtn} style={{ borderColor: C.BORDER, color: C.CORAL }}><Download className="w-4 h-4" /> Scarica QR</button>
              <button data-testid="copy-embed" onClick={copyEmbed} className={secondaryBtn} style={{ borderColor: C.BORDER, color: C.CORAL }}><Code2 className="w-4 h-4" /> Copia codice per il sito</button>
            </div>
          </div>
        </div>
        {!r.payment_connected && <p className="text-xs mt-3" style={{ color: "#C2410C" }}>Il link sarà attivo solo dopo aver collegato i pagamenti qui sotto.</p>}
      </div>

      {/* Stripe connection */}
      <div data-testid="stripe-connect-card" className="bg-white rounded-3xl p-6 sm:p-8" style={softShadow}>
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="w-5 h-5" style={{ color: C.CORAL }} />
          <h2 className="font-display text-2xl font-extrabold" style={{ color: C.INK }}>Incassa le caparre</h2>
        </div>
        <p className="text-sm mb-6" style={{ color: C.MUTED }}>Collega il tuo account Stripe: le caparre arriveranno direttamente sul tuo conto.</p>

        {r.payment_connected ? (
          <div className="rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4" style={{ background: "#E3F1EB" }}>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6" style={{ color: C.GREEN }} />
              <div>
                <p className="font-bold" style={{ color: C.INK }}>Pagamenti attivi</p>
                <p className="text-sm" style={{ color: "#3f5b52" }}>{r.stripe_account_name || "Account Stripe"} · chiave {r.stripe_key_masked}</p>
              </div>
            </div>
            <button data-testid="stripe-disconnect" onClick={disconnectStripe} className="px-4 py-2 rounded-full border-2 font-semibold text-sm transition-colors hover:border-red-400 hover:text-red-500" style={{ borderColor: "#cfe0d8", color: "#3f5b52" }}>
              Scollega
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: "#FEEDE7" }}>
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#C2410C" }} />
              <p className="text-sm" style={{ color: "#5c4a44" }}>
                Il tuo ristorante <b>non è ancora prenotabile</b>. Incolla la tua chiave segreta Stripe per attivare i pagamenti.
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="font-bold hover:underline inline-flex items-center gap-1 ml-1" style={{ color: C.CORAL }}>
                  Dove trovo la chiave? <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Chiave segreta Stripe (sk_...)</label>
              <input data-testid="stripe-key-input" type="password" value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} className={field} placeholder="sk_test_..." />
            </div>
            <button data-testid="stripe-connect-btn" onClick={connectStripe} disabled={connecting} className={`${btnPrimary} w-full`}>
              <CreditCard className="w-4 h-4" /> {connecting ? "Verifica…" : "Collega Stripe"}
            </button>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 space-y-6" style={softShadow}>
        <h2 className="font-display text-2xl font-extrabold" style={{ color: C.INK }}>Dettagli e disponibilità</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Nome</label>
            <input data-testid="set-name" className={field} value={r.name} onChange={(e) => upd("name", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Telefono</label>
            <input data-testid="set-phone" className={field} value={r.phone || ""} onChange={(e) => upd("phone", e.target.value)} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Descrizione</label>
          <textarea data-testid="set-desc" rows={3} className={`${field} resize-none`} value={r.description || ""} onChange={(e) => upd("description", e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Indirizzo</label>
          <input data-testid="set-address" className={field} value={r.address || ""} onChange={(e) => upd("address", e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>URL immagine copertina</label>
          <input data-testid="set-cover" className={field} value={r.cover_image || ""} onChange={(e) => upd("cover_image", e.target.value)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>URL logo (facoltativo)</label>
            <input data-testid="set-logo" className={field} value={r.logo_image || ""} onChange={(e) => upd("logo_image", e.target.value)} placeholder="https://…/logo.png" />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>WhatsApp (per tavoli piccoli)</label>
            <input data-testid="set-whatsapp" className={field} value={r.whatsapp || ""} onChange={(e) => upd("whatsapp", e.target.value)} placeholder="+39 333 1234567" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Colore accento pagina prenotazione</label>
          <p className="text-xs mb-3" style={{ color: C.MUTED }}>Personalizza il colore dei pulsanti e degli accenti che vedono i tuoi clienti quando prenotano.</p>
          <div className="flex flex-wrap items-center gap-3" data-testid="accent-picker">
            {ACCENT_PRESETS.map((c) => {
              const active = (r.accent_color || "#FF5A36").toUpperCase() === c.toUpperCase();
              return (
                <button key={c} type="button" data-testid={`accent-${c}`} onClick={() => upd("accent_color", c)}
                  className="w-9 h-9 rounded-full transition-transform hover:scale-110"
                  style={{ background: c, boxShadow: active ? `0 0 0 3px #fff, 0 0 0 5px ${c}` : "0 1px 4px rgba(0,0,0,0.15)" }}
                  aria-label={`Colore ${c}`} />
              );
            })}
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="accent-custom" type="color" value={r.accent_color || "#FF5A36"} onChange={(e) => upd("accent_color", e.target.value.toUpperCase())}
                className="w-9 h-9 rounded-full border-0 bg-transparent cursor-pointer p-0" style={{ appearance: "none" }} />
              <span className="text-xs font-mono" style={{ color: C.MUTED }}>{(r.accent_color || "#FF5A36").toUpperCase()}</span>
            </label>
          </div>
          <div className="mt-4 rounded-2xl p-4 flex items-center gap-3" style={{ background: "#FBF6EC" }}>
            <span className="text-xs font-semibold" style={{ color: C.MUTED }}>Anteprima:</span>
            <span className="px-5 py-2.5 rounded-full font-bold text-white text-sm" style={{ background: r.accent_color || "#FF5A36" }}>Prenota un tavolo</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Min persone (gruppi)</label>
            <input data-testid="set-minparty" type="number" min="1" className={field} value={r.min_party_size ?? 8} onChange={(e) => upd("min_party_size", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Caparra / persona (€)</label>
            <input data-testid="set-deposit" type="number" min="0" step="0.5" className={field} value={r.deposit_per_person} onChange={(e) => upd("deposit_per_person", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Coperti max / fascia</label>
            <input data-testid="set-covers" type="number" min="1" className={field} value={r.max_covers_per_slot} onChange={(e) => upd("max_covers_per_slot", e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: "#4a453f" }}>Persone max / prenotazione</label>
            <input data-testid="set-maxparty" type="number" min="1" className={field} value={r.max_party_size} onChange={(e) => upd("max_party_size", e.target.value)} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "#4a453f" }}><Clock className="w-4 h-4" /> Orari di apertura</label>
          <p className="text-xs mb-3" style={{ color: C.MUTED }}>Imposta gli intervalli (es. pranzo e cena). Le fasce prenotabili vengono generate ogni 30 minuti.</p>
          <div className="space-y-2 mb-3">
            {(r.opening_ranges || []).map((rg, i) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`range-start-${i}`} type="time" value={rg.start} onChange={(e) => updateRange(i, "start", e.target.value)} className={`${field} max-w-[150px]`} />
                <span style={{ color: C.MUTED }}>→</span>
                <input data-testid={`range-end-${i}`} type="time" value={rg.end} onChange={(e) => updateRange(i, "end", e.target.value)} className={`${field} max-w-[150px]`} />
                <button data-testid={`remove-range-${i}`} onClick={() => removeRange(i)} className="w-9 h-9 rounded-xl bg-white border flex items-center justify-center transition-colors hover:text-red-500 hover:border-red-400" style={{ borderColor: C.BORDER, color: C.MUTED }}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button data-testid="add-range" onClick={addRange} className={secondaryBtn} style={{ borderColor: C.BORDER, color: C.CORAL }}><Plus className="w-4 h-4" /> Aggiungi intervallo</button>
          {generatedSlots.length > 0 && (
            <div className="mt-4">
              <p className="text-xs mb-2" style={{ color: C.MUTED }}>Fasce generate (anteprima):</p>
              <div className="flex flex-wrap gap-2">{generatedSlots.map((t) => (<span key={t} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "#FEEDE7", color: C.CORAL }}>{t}</span>))}</div>
            </div>
          )}
        </div>

        <button data-testid="save-settings" onClick={save} disabled={saving} className={`${btnPrimary} w-full`}>
          <Save className="w-4 h-4" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </button>
      </div>
    </div>
  );
}
